// Parse leve do relatório de medição por ativo da CCEE (separado por ";").
// Espelha a detecção do backend (merx_backend bess/medicao-parser.ts): cabeçalho
// começando em "Ponto de Medição" (BOM tolerado) e ponto de medição na 1ª coluna.

const HEADER_PREFIX = "Ponto de Medição";

const stripBom = (line: string) => line.replace(/^﻿/, "");

const findHeaderIndex = (lines: string[]) =>
  lines.findIndex((line) => stripBom(line).startsWith(HEADER_PREFIX));

export function isCceeMeasurementCsv(content: string): boolean {
  return findHeaderIndex(content.split(/\r?\n/)) !== -1;
}

/**
 * Ponto de medição de uma linha de dados, ou null quando a linha não é um
 * registro de medição (rodapé, linha em branco, texto solto).
 */
function readRecordPoint(line: string): string | null {
  const cols = line.split(";");
  if (cols.length < 6 || !cols[0].trim()) return null;

  const [, , ano] = cols[1].trim().split("/");
  const hora = parseInt(cols[2], 10);
  if (!ano || !Number.isFinite(hora)) return null;

  return cols[0].trim();
}

export function extractMeasuringPoints(content: string): string[] {
  const lines = content.split(/\r?\n/);
  const headerIndex = findHeaderIndex(lines);
  if (headerIndex === -1) return [];

  const points = new Set<string>();

  for (const line of lines.slice(headerIndex + 1)) {
    const point = readRecordPoint(line);
    if (point !== null) points.add(point);
  }

  return Array.from(points);
}

/**
 * Mantém apenas os registros do ponto de medição informado, descartando os das
 * outras unidades. O arquivo não é reserializado: cada linha preservada sai
 * byte a byte como entrou (BOM, preâmbulo, cabeçalho, delimitador, CRLF/LF e
 * newline final), então só muda quais linhas permanecem.
 *
 * Retorna null quando nenhum registro pertence ao ponto — a comparação não deve
 * ser enviada nesse caso.
 */
export function filterMeasurementCsvByPoint(
  content: string,
  pontoMedicao: string
): string | null {
  const target = pontoMedicao.trim();
  // Índices pares são linhas; ímpares, os terminadores originais.
  const parts = content.split(/(\r\n|\n)/);
  const endsWithNewline = /(\r\n|\n)$/.test(content);

  const kept: string[] = [];
  let headerFound = false;
  let matched = 0;

  for (let i = 0; i < parts.length; i += 2) {
    const line = parts[i];
    const terminator = parts[i + 1] ?? "";

    if (!headerFound) {
      if (stripBom(line).startsWith(HEADER_PREFIX)) headerFound = true;
      kept.push(line + terminator);
      continue;
    }

    const point = readRecordPoint(line);
    if (point === null) {
      kept.push(line + terminator);
      continue;
    }
    if (point !== target) continue;

    matched += 1;
    kept.push(line + terminator);
  }

  if (matched === 0) return null;

  const filtered = kept.join("");
  // Descartar a última linha não pode introduzir um newline final que o
  // arquivo original não tinha.
  return endsWithNewline ? filtered : filtered.replace(/(\r\n|\n)$/, "");
}
